// The logic of the dashboard, with no React: what the tiles say and where they lead. The numbers are the API's (GET v3/summary); nothing here
// adds them up again.
import { SEVERITIES, whenText } from './findingModel';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export const count = (table, key) => (table && table[key]) || 0;

// What the dashboard can show: one capability at a time, never combined. Drift is the software drift; the API sends the numbers of each
// (`capabilities.software`, `.patch`, `.cve`, `.reboot`) next to the totals, so nothing here adds them up.
export const VIEWS = [
  { key: 'drift', label: 'Drift', capability: 'software' },
  { key: 'patch', label: 'Patch', capability: 'patch' },
  { key: 'cve', label: 'CVE', capability: 'cve' },
  { key: 'reboot', label: 'Reboot', capability: 'reboot' },
];
export const capabilityOf = (view) => (VIEWS.find((v) => v.key === view) || VIEWS[0]).capability;
const EMPTY = { findings: 0, machines: 0, bySeverity: {}, needsPerson: 0, plansOpen: 0, exceptions: { active: 0, ending: 0, ended: 0 }, worst: null, top: [] };

// The numbers of a summary for a view: that capability's block. A summary kept before the blocks existed shows zeros.
export const scopeOf = (summary, view) => {
  if (!summary) return EMPTY;
  return (summary.capabilities && summary.capabilities[capabilityOf(view)]) || EMPTY;
};

// The capability blocks a view shows, in order, with their titles.
export const BLOCKS = [
  { key: 'software', title: 'Software drift', views: ['drift'], hint: 'Programs that are missing, out of date or not allowed' },
  { key: 'patch', title: 'Patch drift', views: ['patch'], hint: 'Windows updates, restarts and update settings' },
  { key: 'cve', title: 'CVE', views: ['cve'], hint: 'Programs with known vulnerabilities that count under the CVE policy' },
  { key: 'reboot', title: 'Reboot', views: ['reboot'], hint: 'Machines that need a restart, and whether anyone has seen the notices' },
];
export const blocksFor = (view) => BLOCKS.filter((b) => b.views.includes(view));

// The tiles of one summary, in order: each {key, label, value, tone, to} where `to` is the page (with its filters) a click opens. `account` is
// the customer the summary is for (* for the totals, which open each page for the customer chosen on it).
export const tilesOf = (summary, account, capability) => {
  const q = (params) => {
    const pairs = Object.keys(params).filter((k) => params[k]).map((k) => `${k}=${encodeURIComponent(params[k])}`);
    return pairs.length ? `?${pairs.join('&')}` : '';
  };
  const scope = { ...(account && account !== '*' ? { account } : {}), ...(capability ? { capability } : {}) };      // a single capability (CVE) is a filter on the Findings page
  const sev = (s) => ({ key: s, label: s.charAt(0).toUpperCase() + s.slice(1), value: count(summary.bySeverity, s), tone: s, to: `/v3/findings${q({ ...scope, severity: s })}` });
  return [
    ...['critical', 'high', 'medium'].map(sev),
    { key: 'needsPerson', label: 'Needs a person', value: summary.needsPerson || 0, tone: 'amber', to: `/v3/findings${q({ ...scope, status: 'drift' })}`, hint: 'Nothing can be run for these: a person has to look' },
    { key: 'plans', label: 'Plans open', value: summary.plansOpen || 0, tone: 'blue', to: `/v3/actions${q(account && account !== '*' ? { account } : {})}` },
  ];
};

// "As of 2026-11-02 19:52 (2 machines)", or what to do when nothing has been evaluated.
export const asOfText = (last) => {
  if (!last || !last.utc) return 'Not evaluated yet. Run Evaluate on the Findings page.';
  const machines = last.machines !== undefined && last.machines !== null ? ` (${plural(last.machines, 'machine')})` : '';
  return `As of ${whenText(last.utc)}${machines}`;
};

// The customers table of the all-customers view, worst first: most critical, then high, then findings.
export const worstFirst = (accounts) => [...(accounts || [])].sort((a, b) => (
  count(b.bySeverity, 'critical') - count(a.bySeverity, 'critical') || count(b.bySeverity, 'high') - count(a.bySeverity, 'high')
  || (b.findings || 0) - (a.findings || 0) || String(a.account).localeCompare(String(b.account))));

export const severityRank = (s) => SEVERITIES.indexOf(s);

